<template>
  <b-container fluid>
    <b-row class="mb-2">
      <b-col class="px-sm-3 px-0">
        <h1>Kitchen Orders</h1>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="px-sm-3 px-0">
        <b-overlay :show="loading" no-wrap fixed />
        <b-form inline class="mb-2" @submit.prevent="applyFilters">
          <b-form-input
            v-model="queryFilters.search"
            placeholder="Search by name, phone, number or email"
            title="Search by name, phone, number or email"
            class="mr-2 mb-2 single-input"
          />
          <b-form-group class="custom-search-input mr-2 mb-2">
            <multiselect
              v-model="queryFilters.status"
              select-label=""
              open-direction="bottom"
              :options="statusOptions"
              track-by="value"
              deselect-label="remove"
              :searchable="false"
              :close-on-select="true"
              :show-no-results="false"
              :hide-selected="false"
              label="text"
              class="single-input"
              placeholder="Filter by status"
            />
          </b-form-group>
          <custom-search-field
            v-if="hubCount > 1"
            v-model="queryFilters.hub"
            class="mr-2 mb-2 single-input"
            :placeholder="'Filter by hub'"
            :path-to-search="'facilities'"
          />
          <custom-search-field
            v-if="hubCount || kitchenCount > 1"
            v-model="queryFilters.kitchen"
            class="mr-2 mb-2 single-input"
            :placeholder="'Filter by kitchen'"
            :path-to-search="'fulfillment-centers'"
          />
          <custom-date-picker
            :initial-range="queryFilters.range"
            class="mr-2"
            @rangeChange="onRangeChange"
          />
          <b-button type="submit" variant="primary" class="mr-2 mb-2"
            >Filter</b-button
          >
          <b-button variant="secondary" class="mr-2 mb-2" @click="exportOrders"
            >Export</b-button
          >
        </b-form>
      </b-col>
    </b-row>
    <b-row class="overflow-auto">
      <b-col class="px-sm-3 px-0">
        <b-table
          hover
          no-local-sorting
          small
          :fields="fields"
          :items="data"
          :busy="busy"
          @sort-changed="onSortingChanged"
          @row-clicked="onRowClicked"
        >
          <template #head(order_total)>
            <div class="d-flex align-items-center">
              Kitchen Total
              <span id="menu-items-table-availability" class="icon ml-2">
                <img
                  id="hours-tooltip"
                  :src="asset('/imgs/icons/info.svg')"
                  alt="hours info"
                  class="mb-1"
                />
              </span>
            </div>
            <b-tooltip target="menu-items-table-availability" triggers="hover">
              Total represents the subtotal plus tax for each kitchen on each
              order.
            </b-tooltip>
          </template>
          <template #cell(number)="row">
            <router-link :to="`/kitchen-orders/${row.item.id}`">
              {{ row.item.number }}
            </router-link>
          </template>
          <template #cell(status)="row">
            {{ row.item.status | statusFormat }}
          </template>
          <template #cell(fulfillment_time)="row">
            {{ row.item.network_order.fulfillment_time | formatDateTime }}
          </template>
          <template #cell(fulfillment_center)="row">
            <template v-if="row.item.fulfillment_center">
              {{ row.item.fulfillment_center.name }}
            </template>
          </template>
          <template #cell(address)="row">
            <template v-if="row.item.network_order">
              {{ row.item.network_order.address | formatAddressStreet }}
            </template>
          </template>
          <template #cell(order_total)="row">
            {{ (row.item.order_total / 100) | currency }}
          </template>
          <template #cell(facility)="row">
            <template v-if="row.item.network_order.facility">
              {{ row.item.network_order.facility.name }}
            </template>
          </template>
        </b-table>
      </b-col>
    </b-row>
    <pagination-handler v-if="data.length" :query-data="queryFilters" />
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import tableMixin from '../../mixins/TableMixin';
import ResourceAPI from '../../services/ResourceAPI';

export default {
  name: 'KitchenOrders',
  mixins: [tableMixin],
  data() {
    return {
      fields: [
        {
          key: 'number',
          label: 'Order ID',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
        },
        {
          key: 'fulfillment_time',
          label: 'Fulfillment Time',
          sortable: true,
        },
        {
          key: 'address',
          label: 'Address',
          sortable: false,
        },
        {
          key: 'fulfillment_center',
          label: 'Kitchen',
          sortable: true,
        },
        {
          key: 'order_total',
          label: 'Kitchen Total',
          sortable: true,
        },
        {
          key: 'facility',
          label: 'Hub',
          sortable: true,
        },
      ],
      data: [],
      rowRouteName: 'kitchen-order',
      loading: false,
      exportAlert: false,
      statusOptions: [
        {
          value: 'placed',
          text: 'Placed',
        },
        {
          value: 'confirmed',
          text: 'Confirmed',
        },
        {
          value: 'preparing',
          text: 'Preparing',
        },
        {
          value: 'prepared',
          text: 'Prepared',
        },
        {
          value: 'enroute_expo',
          text: 'En Route Expo',
        },
        {
          value: 'compiling',
          text: 'Compiling',
        },
        {
          value: 'enroute_delivery_expo',
          text: 'En Route Delivery Expo',
        },
        {
          value: 'delivery_compiling',
          text: 'Delivery Compiling',
        },
        {
          value: 'enroute_dispatch',
          text: 'Enroute Dispatch',
        },
        {
          value: 'ready',
          text: 'Ready',
        },
        {
          value: 'enroute_customer',
          text: 'Enroute',
        },
        {
          value: 'completed',
          text: 'Completed',
        },
        {
          value: 'canceled',
          text: 'Canceled',
        },
        {
          value: 'refunded',
          text: 'Refunded',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('session', ['hubCount', 'kitchenCount', 'filterPage']),
  },
  watch: {
    hubCount(val) {
      if (val === 1) {
        this.removeTableColumn('hub');
      }
    },
  },
  mounted() {
    if (this.hubCount === 1) {
      this.removeTableColumn('Hub');
    }
    if (this.kitchenCount === 1) {
      this.removeTableColumn('Kitchen');
    }
  },
  methods: {
    onRangeChange(range) {
      this.queryFilters.range = {
        ...range,
      };
    },
    loadResults() {
      this.setRequestData();
      // Format search params for query
      const searchParams = { ...this.queryFilters };
      searchParams.status = searchParams.status
        ? searchParams.status.value
        : null;
      searchParams.fulfillmentCenter = searchParams.kitchen
        ? searchParams.kitchen.id
        : null;
      searchParams.facility = searchParams.hub ? searchParams.hub.id : null;
      searchParams.with =
        'fulfillmentCenter,networkOrder.user,networkOrder.address,networkOrder.facility';
      searchParams.startDate =
        searchParams.range && 'start' in searchParams.range
          ? this.formatDateFilter(searchParams.range.start)
          : null;
      searchParams.endDate =
        searchParams.range && 'end' in searchParams.range
          ? this.formatDateFilter(searchParams.range.end)
          : null;
      searchParams.range = null;
      searchParams.kitchen = null;
      searchParams.hub = null;

      return ResourceAPI.getRecords('fulfillment-center-orders', searchParams)
        .then((data) => {
          this.setResponseData(data);
        })
        .catch((error) => alert(error));
    },
    exportOrders() {
      const me = this;

      me.loading = true;

      ResourceAPI.downloadCSV(
        'reports/fulfillment-center-orders',
        {
          search: me.queryFilters.search,
          fulfillmentCenter: me.queryFilters.kitchen
            ? me.queryFilters.kitchen.id
            : null,
          facility: me.queryFilters.hub ? me.queryFilters.hub.id : null,
          start:
            me.queryFilters.range && 'start' in me.queryFilters.range
              ? this.formatDateFilter(me.queryFilters.range.start)
              : null,
          end:
            me.queryFilters.range && 'end' in me.queryFilters.range
              ? this.formatDateFilter(me.queryFilters.range.end)
              : null,
          status: me.queryFilters.status ? me.queryFilters.status.value : null,
        },
        'kitchen-orders.csv'
      )
        .then(() => {
          me.loading = false;
        })
        .catch((error) => alert(error));
    },
  },
};
</script>
